import { z } from "zod";

/** What kind of short value an ask_text card takes; drives the field's keyboard and check. */
export const AskTextFormat = z.enum(["email", "phone", "date", "url", "number", "text"]);
export type AskTextFormat = z.infer<typeof AskTextFormat>;

export const ASK_TEXT_MAX_LENGTH = 500;
export const ASK_TEXT_QUESTION_MAX_LENGTH = 240;
export const ASK_TEXT_PLACEHOLDER_MAX_LENGTH = 80;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE = /^\+?[\d\s().-]+$/;
const NUMBER = /^[-+]?\d[\d\s]*(?:[.,]\d+)?$/;
const DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * The answer as the bot receives it, or undefined when it does not look like the asked kind.
 * The check is light on purpose: it catches a typo or the wrong field, not every edge case.
 */
export function normalizeAskTextAnswer(format: AskTextFormat, raw: string): string | undefined {
  const value = raw.trim();
  if (!value || value.length > ASK_TEXT_MAX_LENGTH) return undefined;
  if (format === "email") return EMAIL.test(value) ? value : undefined;
  if (format === "phone") {
    const digits = value.replace(/\D/g, "").length;
    return PHONE.test(value) && digits >= 5 && digits <= 15 ? value : undefined;
  }
  if (format === "number") return NUMBER.test(value) ? value : undefined;
  if (format === "date") {
    const match = DATE.exec(value);
    if (!match) return undefined;
    const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
    return date.toISOString().slice(0, 10) === value ? value : undefined;
  }
  if (format === "url") {
    // People type a bare site; read it as HTTPS rather than refusing it.
    const candidate = /^[a-z][a-z\d+.-]*:/i.test(value) ? value : `https://${value}`;
    try {
      const url = new URL(candidate);
      if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
      return url.hostname.includes(".") || url.hostname === "localhost" ? candidate : undefined;
    } catch {
      return undefined;
    }
  }
  return value;
}

export const ASK_FILE_ACCEPT_MAX_LENGTH = 200;

/**
 * Whether a file fits an ask_file card's accept list, written like an HTML accept attribute
 * ("image/*,.pdf"). An empty list takes any file the chat accepts.
 */
export function askFileAccepts(
  accept: string | undefined,
  file: { name: string; mimeType: string },
): boolean {
  const tokens = (accept ?? "")
    .split(",")
    .map((token) => token.trim().toLowerCase())
    .filter(Boolean);
  if (!tokens.length) return true;
  const name = file.name.toLowerCase();
  const mimeType = file.mimeType.toLowerCase();
  return tokens.some((token) =>
    token.startsWith(".")
      ? name.endsWith(token)
      : token.endsWith("/*")
        ? mimeType.startsWith(token.slice(0, -1))
        : mimeType === token,
  );
}
